import { motion } from "framer-motion";
import { Check } from "lucide-react";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import heroImg from "@/assets/inspection-hero.webp";
import portraitImg from "@/assets/inspector-portrait.webp";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { inspectionFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as const },
};

const forensicCards = [
  {
    icon: "🔬",
    title: "Forensic Failure Analysis",
    desc: "We identify the exact root cause. Whether it's a manufacturing defect, improper installation, subfloor moisture intrusion, or environmental stress. No guessing, no opinions, just data.",
  },
  {
    icon: "💧",
    title: "Advanced Moisture Testing",
    desc: "We use non-destructive digital meters and ASTM-standard relative humidity testing to prove precisely where and how moisture compromised the wood. The kind of evidence that holds up in court.",
  },
  {
    icon: "⚖️",
    title: "Dispute Resolution",
    desc: "Our reports are industry-recognized and unbiased. They force manufacturers, insurance companies, or negligent contractors to take responsibility. Our findings are rooted in science, not speculation.",
  },
  {
    icon: "📋",
    title: "Expert Witness & Litigation",
    desc: "We provide detailed written reports and expert testimony that bridges the gap between complex flooring science and the courtroom. Our credentials speak for themselves.",
  },
];

const credentials = [
  "NWFA Certified Wood Flooring Inspector. The industry's most rigorous credential, maintained through continuous advanced education.",
  "Decades of hands-on installation, sanding, and finishing. We've seen every failure mode firsthand.",
  "Licensed Michigan Residential Builder. We understand structural integrity at a level most inspectors simply can't.",
  "Trusted by insurance adjusters, builders, and legal professionals across the state.",
  "Strict adherence to the NWFA Code of Conduct. Every inspection is 100% unbiased and fact-based.",
];

const Inspections = () => {
  usePageSEO({
    title: "NWFA Certified Wood Flooring Inspector | Expert Witness",
    description: "NWFA Certified wood flooring inspector — forensic failure analysis, expert witness reports, dispute resolution. Independent third-party assessment.",
    path: "/inspections",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>

      {/* ══ 1 · HERO ══ */}
      <section className="relative h-[70svh] min-h-[480px] max-h-[720px] flex items-end overflow-hidden">
        <PageBreadcrumb variant="overlay" items={[
          { label: "Home", to: "/" },
          { label: "Services" },
          { label: "Inspections" },
        ]} />
        <div className="absolute inset-0">
          <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
            src={heroImg}
            alt="Moisture meter pins pressed into an oak floor during an inspection"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-foreground/10" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
        >
          <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
            <span className="w-7 h-px bg-primary inline-block" />
            NWFA Certified Inspections · Metro Detroit, Michigan
          </p>
          <h1 className="font-display text-[clamp(30px,6.5vw,72px)] font-semibold leading-[1.05] tracking-tight text-primary-foreground">
            Independent NWFA Certified Hardwood Floor Inspections
          </h1>
          <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
            Forensic failure analysis, dispute resolution, and expert testimony for Metro Detroit homeowners, builders, and attorneys.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
            <QuoteModal
              trigger={
                <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                  Schedule a Call →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
            >
              Call (248) 325-8853
            </a>
          </div>
        </motion.div>
      </section>

      {/* ══ 1b · NWFA AUTHORITY CALLOUT ══ */}
      <section className="bg-foreground py-14 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-[hsl(var(--gold-400))] mb-4">
              The Gold Standard
            </p>
            <h2 className="font-display text-[clamp(26px,4.5vw,48px)] font-semibold text-primary-foreground leading-[1.1] mb-6">
              The Industry's Highest{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">Authority.</em>
            </h2>
            <p className="text-sm md:text-[15px] font-light text-primary-foreground/55 leading-relaxed">
              The National Wood Flooring Association sets the definitive standards for the global wood flooring industry. Their Certified Inspector credential is the most difficult certification to achieve. It requires years of intensive study, rigorous testing, decades of field experience, and continuous mandatory education to maintain. We hold this credential because our clients deserve nothing less.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══ 2 · THE BLAME GAME ══ */}
      <section className="bg-background py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl">
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
              The Problem
            </p>
            <h2 className="font-display text-[clamp(26px,4.5vw,52px)] font-semibold text-foreground leading-[1.1]">
              When a floor fails,{" "}
              <em className="italic font-normal text-primary">the blame game begins.</em>
            </h2>
            <p className="mt-6 text-sm md:text-[15px] font-light text-muted-foreground leading-relaxed">
              Cupping, buckling, excessive gaps, peeling finish  these failures are devastating. And when they happen, homeowners often find themselves caught in the middle: the retailer blames the installer, the installer blames the manufacturer, and the manufacturer blames your HVAC system.
            </p>
            <p className="mt-4 text-sm md:text-[15px] font-light text-muted-foreground leading-relaxed">
              That's where we step in. As Michigan's NWFA Certified Inspector, Ed Malaski provides unbiased, scientific analysis to uncover the absolute truth. Operating under a strict Code of Conduct, every inspection delivers only fact-based, scientifically proven findings  never opinions.
            </p>
            <div className="flex flex-wrap gap-2 mt-8">
              {["Unbiased Analysis", "Scientific Methods", "ASTM Standards", "Court-Ready Reports"].map((chip) => (
                <span
                  key={chip}
                  className="text-[9px] font-bold tracking-[.1em] uppercase text-primary border border-border rounded-md px-3 py-1.5"
                >
                  {chip}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ 3 · FORENSIC SERVICES GRID ══ */}
      <section className="bg-foreground py-14 md:py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <motion.div {...fadeUp} className="mb-8 md:mb-12 max-w-2xl">
            <p className="text-[11px] font-semibold tracking-[.1em] uppercase text-[hsl(var(--gold-400))] mb-2 flex items-center gap-2">
              Inspection Services
              <span className="flex-1 h-px bg-primary-foreground/10" />
            </p>
            <h2 className="font-display text-[clamp(28px,5vw,52px)] font-semibold text-primary-foreground leading-[1.1]">
              Forensic-Grade{" "}
              <em className="italic font-normal text-[hsl(var(--gold-300))]">Inspections.</em>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {forensicCards.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="border border-primary-foreground/10 rounded-2xl p-6 md:p-8 flex flex-col group hover:bg-primary-foreground/[.06] transition-colors duration-500"
              >
                <div className="text-3xl mb-4">{card.icon}</div>
                <h4 className="font-display text-lg md:text-xl font-semibold text-primary-foreground mb-3 leading-snug">
                  {card.title}
                </h4>
                <p className="text-[12px] md:text-[13px] font-light text-primary-foreground/50 leading-relaxed flex-grow">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 4 · CREDENTIALS ══ */}
      <section className="bg-background py-16 md:py-24 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center">
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="relative order-2 md:order-1">
              <div className="rounded-2xl overflow-hidden relative group">
                <img width={800} height={534} loading="lazy"
                  src={portraitImg}
                  alt="Ed Malaski, NWFA Certified Wood Flooring Inspector"
                  className="w-full aspect-[4/5] object-cover transition-all duration-700 ease-out grayscale-[20%] contrast-[1.1] group-hover:grayscale-0 group-hover:scale-[1.04]"
                />
              </div>
            </motion.div>

            <motion.div {...fadeUp} className="order-1 md:order-2">
              <p className="text-[10px] md:text-[11px] font-semibold tracking-[.18em] uppercase text-primary mb-4">
                Credentials That Hold Up In Court
              </p>
              <h2 className="font-display text-[clamp(26px,4vw,44px)] font-semibold text-foreground leading-[1.12]">
                Why hire 1 Wood Floors{" "}
                <em className="italic font-normal text-primary">for your inspection?</em>
              </h2>
              <p className="mt-5 text-sm md:text-[15px] font-light text-muted-foreground leading-relaxed">
                In a flooring dispute, assumptions and opinions mean nothing. Only scientific facts matter. We rely on data, NWFA standards, and undeniable credentials to establish the truth:
              </p>
              <ul className="mt-6 flex flex-col gap-3">
                {credentials.map((cred) => (
                  <li key={cred} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center mt-0.5">
                      <Check className="w-3 h-3 text-primary" />
                    </span>
                    <span className="text-[13px] md:text-sm font-light text-foreground/80 leading-relaxed">
                      {cred}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ 5 · CTA BANNER ══ */}
      <section className="bg-primary py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
              Stop guessing. Get certified proof.
            </h3>
            <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
              If your hardwood floor is failing, time is of the essence. Connect with us for an independent, scientific evaluation and protect your investment.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
            <QuoteModal
              trigger={
                <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                  Schedule a Consultation →
                </button>
              }
            />
            <a
              href="tel:2483258853"
              className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
            >
              📞 (248) 325-8853
            </a>
          </div>
        </div>
      </section>

        <RelatedServices
          heading="After the inspection, here's what comes next."
          subheading="Related Services"
          links={[
            { title: "Subfloor Leveling & Repair", desc: "Inspection revealed subfloor damage? Our crew corrects structural issues before any flooring goes down.", to: "/subfloor-leveling", tag: "Service" },
            { title: "Dust Control Refinishing", desc: "Floor needs restoration, not replacement? Our 98% containment refinishing brings it back to life.", to: "/refinishing", tag: "Service" },
            { title: "Hardwood Installation", desc: "Ready for new hardwood after an inspection clears the way? NWFA Certified Installers handle every step.", to: "/installation", tag: "Service" },
            { title: "Environment & Acclimation", desc: "Many floor failures trace back to moisture imbalance. Learn the science behind proper acclimation.", to: "/environment-acclimation", tag: "Technical" },
            { title: "Michigan Plank Flooring", desc: "Replacing a failed floor? Upgrade to bespoke European White Oak engineered for your specific environment.", to: "/michigan-plank-flooring", tag: "Brand" },
          ]}
        />

        <GoogleReviewsCarousel contextTags={["inspection"]} count={4} />
        <PageFAQ suppressJsonLd faqs={inspectionFAQs} heading={<>Inspection questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Inspection FAQs" />
      </main>
      <SiteFooter />
    </div>
  );
};

export default Inspections;
